/* eslint-disable class-methods-use-this */
/**
 * All the things required to make new tutorial pages work
 */
import { select, selectAll, createElement } from 'global/utils/dom';

export default class TutorialsShow {
  constructor() {
    /** @desc state relevant */
    this.state = {
      current: null,
      next: null,
      prev: null,
      curStep: 0,
      markers: [], // heading used as section makers
      steppedViewMode: true,
      helpboxesTogglers: [],
      doAllMarkersHaveIds: true, // we assume yes, but check later
    };

    /** @desc DOM Target Elements/Container */
    this.tutorialContentWrapper = select('[data-js-docs-article-content]');
    this.viewModeTogglerContainer = select('[data-tutorials-view-mode-toggler]');
    this.TOCmenuContainer = select('[data-tutorials-toc-menu]');
    this.prevNextMenuContainer = select('[data-tutorials-prev-next-menu]');
    this.prevNextMenu = null;
    this.TOCMenu = null;
    this.TOCTogglers = null;
    this.TOCPopup = null;
    this.viewModeToggler = null;
    this.promoBannerRef = select('[data-js-hook="promo-banner"]');
  }

  /**
   * A readonly proper used for generated markers
   * id prefix
   */
  static stepPrefix = 'tutorial-step';

  // /////////////////////////////////////////////////
  // TUTORIAL CONTENT EXTRACTION
  // /////////////////////////////////////////////////

  /**
   * @desc searches rendered document markup and extracts each
   * tutorial step form h2 headings, with an exception for the
   * the first step which always use h1
   * @return {Array}
   */
  getMarkers = () => {
    const markers = selectAll(':scope > h1, :scope > h2', this.tutorialContentWrapper);
    this.state.markers.push(...markers);
    return markers;
  };

  /**
   * @desc loops over extracted markers them and wraps all content
   * till next marker into a <section> container. This will make possible
   * to hide/show all content related to markers based on current step
   * @return {Array} ofr parent elements (<section>)
   */
  wrapMarkersInSteps = () => {
    return this.state.markers.map((marker, i, markers) => {
      const $next = markers[i + 1];
      const wrappingDiv = `<section data-js-generated id="section-${TutorialsShow.stepPrefix}-${i}" />`;

      // wrap steps in sections
      $(marker).nextUntil($next, ':not(script)').addBack().wrapAll(wrappingDiv);

      // if tutorial author didn't write a unique heading id for
      // the marker, we generate one, to keep navigation working
      if (!marker.id) {
        marker.id = `${TutorialsShow.stepPrefix}-${i}`;
        this.state.doAllMarkersHaveIds = false;
      }

      return marker.parentElement;
    });
  };

  // /////////////////////////////////////////////////
  // MENUS NAVIGATION: TOC AND PREVNEXT MENU
  // /////////////////////////////////////////////////

  /**
   * Creates table of contents menu (for sidebar), appends it to
   * the target DOM container and adds required event listeners
   */
  createTOCMenu = () => {
    this.TOCMenu = createElement(`
      <ul class="c-tutorials-sidebar__toc-menu" data-jump-menu data-jump-menu-no-hidden-links>
      ${this.state.markers
        .map((m) => {
          const linkLabel = this.getMarkerTitle(m);
          return `
            <li class="c-tutorials-sidebar__toc-menu-item">
              <a class="c-tutorials-sidebar__toc-menu-link c-link--slide-in-icon"
                href="#${m.id}">
                <svg class="ai ai-arrow-right c-link__icon" role="presentation">
                  <use xlink:href="#ai-arrow-right"></use>
                </svg>
                <span class="c-link__text">${linkLabel}</span>
              </a>
            </li>
          `;
        })
        .join(' ')}
      </ul>
    `);

    this.TOCmenuContainer.parentNode.replaceChild(this.TOCMenu, this.TOCmenuContainer);
    this.TOCMenu.addEventListener('click', this.handleMenuClick);
  };

  /**
   * Creates sticky navigation with relevant link
   * Based on loaded step and
   */
  createPrevNextMenu = () => {
    const prev = this.state.prev && this.state.prev.id;
    const next = this.state.next && this.state.next.id;

    this.prevNextMenu = createElement(`
      <nav class="c-tutorials-prev-next-menu" data-tutorials-injected-prev-next-menu>
        <a href="#${prev}"
          class="
            c-tutorials-step-nav__btn
            c-button c-button--primary-ghost
            ${!prev && 'u-hidden'}
          "
          data-js-button-prev
        >
          <svg class="ai ai-chevron-up ai-rotate-90 ai-8dp c-button__icon" role="presentation" data-js-button-icomn>
            <use xlink:href="#ai-chevron-up"></use>
          </svg>
          <span class="c-button__text" data-js-button-text>Previous</span>
        </a>

        <button data-tutorials-toc-toggler class="
          c-tutorials-toc-toggler
          c-button c-button--icon c-button--secondary
          "
        >
          <svg class="ai ai-bars c-button__icon" role="presentation">
            <use xlink:href="#ai-bars"></use>
          </svg>
          <span class="c-button__text u-sr-only">Toggle menu</span>
        </button>

        <a href="#${next}"
          class="
            c-tutorials-step-nav__btn
            c-button c-button--primary
            ${!next && 'u-hidden'}
          "
          data-js-button-next
        >
          <span class="c-button__text" data-js-button-text>Next</span>
          <svg class="ai ai-chevron-up ai-rotate90 ai-8dp c-button__icon" role="presentation" data-js-button-icon>
            <use xlink:href="#ai-chevron-up"></use>
          </svg>
        </a>
      </nav>
    `);

    this.prevNextMenu.addEventListener('click', this.handleMenuClick);
    // replace container with actual menu
    this.prevNextMenuContainer.parentNode.replaceChild(this.prevNextMenu, this.prevNextMenuContainer);

    // save reference to inner parts of the buttons for content update
    const prevBtnRef = select('[data-js-button-prev]', this.prevNextMenu);
    const nextBtnRef = select('[data-js-button-next]', this.prevNextMenu);
    this.prevBtn = {
      ref: prevBtnRef,
      text: select('[data-js-button-text]', prevBtnRef),
      icon: select('[data-js-button-icon]', prevBtnRef),
    };

    this.nextBtn = {
      ref: nextBtnRef,
      text: select('[data-js-button-text]', nextBtnRef),
      icon: select('[data-js-button-icon]', nextBtnRef),
    };
  };

  popStateHandler = (e) => {
    $(e.state.elemId).closest('section').removeClass('u-hidden').siblings().addClass('u-hidden');
    this.setCurrentStep();
  };

  /**
   * It's used by both TOC and prevNextBtn
   * the target DOM container and adds required event listeners
   * @todo since we're listening for popstate event already
   * and the click on a link with href already triggers it, we should
   * probably simplify this. Not
   */
  handleMenuClick = (e) => {
    e.preventDefault();
    const link = e.target.closest('a');
    if (!link) return;

    // empty hash means it's not a step, but normal url
    // since preventDefault will prevent the click trigger
    // we manipulate location directly
    if (link.hash === '') {
      window.location = link.href;
      window.history.pushState({}, 'current', link.href);
      return;
    }

    window.history.pushState({ elemId: link.hash }, '', link.hash);

    link.blur();

    this.setCurrentStep(link.hash);
  };

  /**
   * Update links copy on lang change
   * Since each can have multiple titles for each language
   */
  updateTOCMenu = () => {
    const links = selectAll('a', this.TOCMenu);
    const labels = this.state.markers.map((marker) => {
      return this.getMarkerTitle(marker);
    });

    links.forEach((link, i) => {
      link.lastElementChild.textContent = labels[i];
    });
  };

  /**
   * add active state to TOC menu links
   */
  setTOCMenuCurrent = () => {
    const links = selectAll('a', this.TOCMenu);
    const activeLink = links.find((l) => l.hash === `#${this.state.current.id}`);

    links.map((l) => l.classList.remove('is-active'));
    activeLink.classList.add('is-active');
  };

  /**
   * Hides show based on the existence of prev and next steps
   * updated link href according to the step
   */
  updatePrevNextMenu = () => {
    const prevId = this.state.prev && this.state.prev.id;
    const nextId = this.state.next && this.state.next.id;

    this.prevBtn.ref.href = `#${prevId}`;
    this.nextBtn.ref.href = `#${nextId}`;

    if (prevId) {
      const prevText = this.getMarkerTitle(this.state.prev);
      this.prevBtn.text.textContent = prevText;
      this.prevBtn.ref.classList.remove('u-hidden');
    } else {
      this.prevBtn.ref.classList.add('u-hidden');
    }

    if (nextId) {
      const nextText = this.getMarkerTitle(this.state.next);

      this.nextBtn.icon.classList.remove('u-hidden');
      this.nextBtn.text.textContent = nextText;
      this.nextBtn.ref.classList.remove('u-hidden');
      this.nextBtn.ref.classList.remove('is-last');
    } else {
      this.nextBtn.ref.classList.remove('u-hidden');
      this.nextBtn.ref.classList.add('is-last');

      this.nextBtn.ref.href = '/tutorials';
      this.nextBtn.text.textContent = 'Find more tutorials';
      this.nextBtn.icon.classList.add('u-hidden');
    }
  };

  /**
   * @desc Updates state with current step and its
   * sideEffects next, previous
   * @note [1] -  account for invalid section title hash urls
   * ex: tutorials/name#unexistent-hash will be ignored
   * and first step will be loaded, otherwise we got and empty
   * tutorial
   *
   * @param {String} hashTarget - a specific hashId
   */
  setCurrentStep = (hashTarget) => {
    let target = hashTarget;
    const { hash } = window.location;

    // if not argument provided default to window hash
    if (!target) {
      target = hash;
    }

    const stepIndex = this.state.markers.findIndex((s) => `#${s.id}` === target);

    // @note[1]
    if (target !== '' && stepIndex >= 0) {
      this.state.curStep = stepIndex;
      this.state.current = this.state.markers[stepIndex];
    } else {
      this.state.curStep = 0;
      // eslint-disable-next-line
      this.state.current = this.state.markers[0];
    }

    // if undefined (-1 or > this.state.markers.lenght), we will then hide buttons
    this.state.next = this.state.markers[this.state.curStep + 1];
    this.state.prev = this.state.markers[this.state.curStep - 1];

    // dispatch a custom event, so other parts of the app
    // can react to this change
    const event = new CustomEvent('tutorials:set-current', {});
    document.dispatchEvent(event);
  };

  // /////////////////////////////////////////////////
  // VIEW MODE
  // /////////////////////////////////////////////////

  /**
   * Verify if there's a preferred view mode saved in localStorage
   * and. If yes update default state, else setViewMode based
   * on default this.state.
   * Required side effects: update viewModeToggler text based
   * on current viewMode
   * Update DOM: if stepped, hide all but current step, else show
   * all the things
   */
  checkInViewMode = () => {
    const preference = this.getViewModePref();

    // if preference on storage
    if (preference !== null) {
      this.state.steppedViewMode = JSON.parse(preference);
    } else {
      // else set default state preference
      this.setViewModePref(this.state.steppedViewMode);
    }

    // dispatch on page load
    document.dispatchEvent(
      new CustomEvent('tutorials:update-view-mode', {
        detail: { steppedViewMode: this.state.steppedViewMode },
      }),
    );
  };

  /**
   * Sets viewmode preference in localStorage
   * @param {Boolean} viewMode
   */
  setViewModePref = (viewMode) => {
    localStorage.setItem('tutorialsViewModePref', viewMode);
  };

  /**
   * Gets preferred view mode from localStorage
   */
  getViewModePref = () => {
    return localStorage.getItem('tutorialsViewModePref');
  };

  /**
   * Displays appropriate button label and icon based on selected
   * viewmode: label should be the opposite of selected.
   * @note button is already at the DOM on page load
   */
  updateViewModeTogglerLabel = () => {
    const { 0: svgIcon, 1: text } = [...this.viewModeToggler.children];
    const svgIconUse = svgIcon.firstElementChild;

    if (this.state.steppedViewMode) {
      svgIcon.classList.remove('ai-library');
      svgIcon.classList.add('ai-doc-list');
      svgIconUse.setAttribute('xlink:href', '#ai-doc-list');
      text.textContent = 'View as single page';
    } else {
      svgIcon.classList.remove('ai-doc-list');
      svgIcon.classList.add('ai-library');
      svgIconUse.setAttribute('xlink:href', '#ai-library');
      text.textContent = 'View as multiple pages';
    }
  };

  /**
   * We only have 2 view modes "full page" or "stepped"
   * So a boolean will toggle one or another
   */
  toggleViewMode = () => {
    this.state.steppedViewMode = !this.state.steppedViewMode;
    // save to preferences
    this.setViewModePref(this.state.steppedViewMode);

    // dispatch event
    document.dispatchEvent(
      new CustomEvent('tutorials:update-view-mode', {
        detail: { steppedViewMode: this.state.steppedViewMode },
      }),
    );
  };

  /**
   *
   */
  createViewModeToggler = () => {
    this.viewModeToggler = createElement(`
      <button class="c-button c-button--secondary c-button--naked c-tutorials-viewmode-toggler"
        data-js-injected-view-mode-toggler>
        <svg class="ai ai-doc-list c-button__icon" role="presentation">
          <use xlink:href="#ai-doc-list"></use>
        </svg>
        <span class="c-button__text">View as single page</span>
      </button>
    `);

    // add events
    this.viewModeToggler.addEventListener('click', this.toggleViewMode);

    // replace container with actual menu
    this.viewModeTogglerContainer.parentNode.replaceChild(this.viewModeToggler, this.viewModeTogglerContainer);
  };

  // /////////////////////////////////////////////////
  // TUTORIAL STEP HELPBOXES
  // /////////////////////////////////////////////////

  /**
   * Get helpbox present at the end of each tutorial step.
   * @requires bootstrap-collapse
   * @note boxSelector ".docs-helpbox" is defined in raw textile (ably-docs gem)
   * @param {*} section - wrapping element over step
   */
  getHelpbox = (section) => {
    const box = select('.docs-helpbox', section);
    // might not have help step
    if (box) {
      box.classList.add('c-tutorials-helpbox');
      box.id = `helpbox-${section.id}`;
    }

    return box;
  };

  /**
   * @requires bootstrap-collapse
   * @param {String} targetId - id of the box to hide
   */
  createHelpboxToggler = (targetId) => {
    return createElement(`
      <button class="c-button c-button--lightsmoke c-tutorials-helpbox-toggler"
        data-toggle="collapse" data-target="#${targetId}"
        aria-expanded="false"
        aria-controls="${targetId}">
        <svg class="ai ai-help c-button__icon" role="presentation">
          <use xlink:href="#ai-help"></use>
        </svg>
        <span class="c-button__text">Need Help?</span>
        <svg class="ai ai-chevron-down c-button__icon" role="presentation">
          <use xlink:href="#ai-chevron-down"></use>
        </svg>
      </button>
    `);
  };

  /**
   * By design on stepped view mode helpbox are shown by default
   * and toggler button is hidden. Full page mode does the opposite
   */
  updateHelpboxesVisibilityForViewMode = () => {
    if (this.state.steppedViewMode) {
      this.state.helpboxesTogglers.forEach((t) => t.classList.add('u-hidden'));
      this.state.helpboxes.forEach((box) => box.classList.remove('collapse'));
    } else {
      this.state.helpboxesTogglers.forEach((t) => t.classList.remove('u-hidden'));
      this.state.helpboxes.forEach((box) => box.classList.add('collapse'));
    }
  };

  /**
   * @desc Gathers and set ups helpboxes for all steps which includes
   *  the creation of toggler button to collapse/show them on multipage
   *  view mode
   * @param {Array} sectionEls - list of wrapping section elements
   */
  setupHelpboxes = (sectionEls) => {
    // get and filter null values (no helpbox)
    this.state.helpboxes = sectionEls.map(this.getHelpbox).filter(Boolean);

    const togglers = [];
    this.state.helpboxes.forEach((box) => {
      const toggler = this.createHelpboxToggler(box.id);
      togglers.push(toggler);
      box.parentElement.insertBefore(toggler, box);
    });

    this.state.helpboxesTogglers = [...togglers];

    this.updateHelpboxesVisibilityForViewMode();
  };

  // /////////////////////////////////////////////////
  // GENERIC HELPERS
  // /////////////////////////////////////////////////

  /**
   * Helper to scrollToCurrent step whe in
   * So a boolean will toggle one or another
   */
  scrollToCurrentStep = (immediate = false) => {
    if (this.state.steppedViewMode) {
      window.scrollTo(0, 0);
    } else {
      const headerEl = select('#meganav header');
      const headerOffset = headerEl ? headerEl.offsetHeight + 15 : 15;
      const currentOffsetTop = $(this.state.current).offset().top;
      const scrollPos = currentOffsetTop - headerOffset;

      if (immediate) {
        window.scrollTo(0, scrollPos);
      } else {
        $('html, body').animate(
          {
            scrollTop: scrollPos,
          },
          600,
        );
      }
    }
  };

  /**
   * Triggers recalculations on page elements using StickySidebar plugin
   * Classic timeout required :shrug:
   */
  updateAppElementsStickyness = () => {
    setTimeout(() => {
      window.sidebarInstances.forEach((sticky) => {
        sticky.updateSticky();
      });
    }, 20);
  };

  /**
   * @desc titles can be different based on code language.
   *    This extracts correct one.
   * @note [1] - sometimes titles have <span class="caps"></span> 🤷‍♂️
   * @param {Element} marker - the heading element for the step
   * @returns {String} - The appropriate title
   */
  getMarkerTitle = (marker) => {
    if (marker.dataset.tutorialTitle === '') {
      return 'Overview';
    }

    const markerMultiLangTitles = selectAll('[data-lang-content-group]', marker);

    if (markerMultiLangTitles.length > 0) {
      const visible = markerMultiLangTitles.find((el) => el.style.display === '');
      return visible ? visible.textContent : markerMultiLangTitles[0].textContent;
    }
    // else
    return marker.textContent; // [1]
  };

  // /////////////////////////////////////////////////
  // EVENT HANDLERS
  // /////////////////////////////////////////////////

  /**
   * what needs to be executed when a user changes tutorial language
   * when language is changed (on language.js.coffee) it emits an event
   * and we need to do some tasks based on that event
   * @param {Event} e
   */
  // eslint-disable-next-line
  handleDocsLanguageChange = (e) => {
    // we need to update labels
    this.updateTOCMenu();
  };

  /**
   * @note [2] - handle legacy documentation + website default scrollTo mechanisms
   *   that will fail because content is not yet parsed and therefore elements are
   *   not at their final positions
   * @param {Event} e
   */
  // eslint-disable-next-line
  handlePageLoad = (e) => {
    if (!this.state.steppedViewMode) {
      setTimeout(this.scrollToCurrentStep, 350);
    }

    // Store the initial content
    window.history.replaceState(
      {
        elemId: `#${$('[data-js-generated] ').not('.u-hidden').find('h1, h2').attr('id')}`,
      },
      document.title,
      document.location.href,
    );
  };

  /**
   * handler custom tutorials:set-current dispatched event
   * @note [1] - hidden codeblocks (on hidden steps) are not parsed for
   *    performance reasons, we need to run highlighter again
   *    @see code_window.js.coffee#addSyntaxHighlightingToCodeBlocks
   */
  handleSetCurrent = () => {
    this.setTOCMenuCurrent();

    // update nextPrevLabels
    this.updatePrevNextMenu();

    // show/hide section based on user selected
    // view mode
    this.renderDOM();

    // @note [1]
    // eslint-disable-next-line
    CodeWindow.addSyntaxHighlightingToCodeBlocks();
  };

  /**
   * @desc toggles scroll event listers on the menu based on view mode
   *    setsCurrentStep in case it has changed because user scrolled
   * @note [1] - show hide for single page mode. Above is not used because
   *    setCurrentStep triggers renderDOM as sideffect. Passed context argument
   *    to make scrollToCurrentStep immediate after changing view mode
   *   // add context argument
   * @note [2] - hide promo banner on multi page tutorials
   *   https://github.com/ably/website/pull/2866#issuecomment-560320351
   *   buf only if it it exists on the dom: when user is loggedIn, promo banner is
   *   not rendered serverside
   * @requires window.DocMenus - menus.js.coffee
   * @param {Event} e - Custom event emitted, contains the viewMode current state
   */
  handleUpdateViewMode = (e) => {
    const { steppedViewMode } = e.detail;

    if (steppedViewMode) {
      const activeStepOnScroll = select('.is-active', this.TOCMenu).hash;
      // someone clicked copy to clipboard
      if (this.externalChange) {
        // set it to current window.hash
        this.setCurrentStep();
        this.externalChange = false;
      } else {
        this.setCurrentStep(activeStepOnScroll);
      }
      window.DocMenus.destroyJumpMenu();

      // styling context
      this.tutorialContentWrapper.setAttribute('data-js-docs-article-content', 'multi-page');

      if (this.promoBannerRef) this.promoBannerRef.setAttribute('hidden', ''); // [2]
    } else {
      window.DocMenus.setupJumpMenu();
      // styling context
      this.tutorialContentWrapper.setAttribute('data-js-docs-article-content', '');

      if (this.promoBannerRef) this.promoBannerRef.removeAttribute('hidden'); // [2]

      // @note [1]
      this.renderDOM('update-view-mode');
    }

    // update toggler button label accorddinly
    this.updateViewModeTogglerLabel();
  };

  /**
   * Callbacks for render DOM things
   * @note [1] - Side effect: helpboxes and its toggler buttons
   *  need to be visible/collapsed based on view mode
   * @note [2] - Side effect: Due to DOM size changes on toggling,
   *  sticky elements (tocMenu) get's glitchy, so we need to update those
   */
  handleRenderDOM = (e) => {
    const { context } = e.detail;

    this.updateHelpboxesVisibilityForViewMode(); // [1]
    this.updateAppElementsStickyness(); // [2]

    const doImmediateScroll = context === 'update-view-mode';
    this.scrollToCurrentStep(doImmediateScroll);
  };

  /**
   *
   * @see handleUpdateViewMode
   */
  // eslint-disable-next-line
  handleExternalManipulationsOfLocation = (e) => {
    // signal external change to other methods can react to it
    this.externalChange = true;
  };

  /**
   * Can only be called after the creation of dyanmic menus
   * allows to toggle TOC nav on mobile as a popup
   */
  setUpMobileHelpers = () => {
    this.TOCTogglers = selectAll('[data-tutorials-toc-toggler]');
    this.TOCPopup = select('[data-tutorials-toc-popup]');

    this.TOCTogglers.forEach((toggler) => {
      toggler.addEventListener('click', () => {
        this.TOCPopup.classList.toggle('is-open');
      });
    });
  };

  /**
   * @desc Attach global events relevant to functionality
   */
  addDomEvents = () => {
    // when page loads
    window.addEventListener('load', this.handlePageLoad);
    // when a new code language is set
    document.addEventListener('docs:set-language', this.handleDocsLanguageChange);
    // when a current step function is called
    document.addEventListener('tutorials:set-current', this.handleSetCurrent);
    // when view mode is changed from single to multi page and vice-versa
    document.addEventListener('tutorials:update-view-mode', this.handleUpdateViewMode);

    document.addEventListener('tutorials:render-dom', this.handleRenderDOM);

    document.addEventListener('docs:anchor-copy-to-clipboard', this.handleExternalManipulationsOfLocation);

    // adds events to TOC togglers
    this.setUpMobileHelpers();

    // adds events to browser back/forward actions
    window.addEventListener('popstate', this.popStateHandler);
  };

  // /////////////////////////////////////////////////
  //    MAIN: PARSE AND RENDER
  // ////////////////////////////////////////////////

  /**
   * Parses rendered textile document and extracts and
   * applies dom structures to achieve full tutorial Functionality
   */
  parseTutorialContentAndBuildDOM = () => {
    this.getMarkers();
    // executes replacement and returns each parent section
    const sections = this.wrapMarkersInSteps();
    this.createTOCMenu();
    this.createPrevNextMenu();
    this.createViewModeToggler();
    this.setupHelpboxes(sections);
    this.addDomEvents();
  };

  /**
   * Hides show appropriate UI elements based on current
   * Viewmode
   * @param {String} - context, who called render DOM?
   */
  renderDOM = (context) => {
    if (this.state.steppedViewMode) {
      this.state.markers.forEach((s) => s.parentElement.classList.add('u-hidden'));
      this.state.current.parentElement.classList.remove('u-hidden');
      this.prevNextMenu.classList.remove('u-hidden');
    } else {
      // hide sticky bottom nav
      this.prevNextMenu.classList.add('u-hidden');
      // show all
      this.state.markers.forEach((s) => s.parentElement.classList.remove('u-hidden'));
    }

    document.dispatchEvent(
      new CustomEvent('tutorials:render-dom', {
        detail: { context },
      }),
    );
  };

  /**
   * Executes all things necesseray for this to work
   */
  init = () => {
    this.parseTutorialContentAndBuildDOM();
    this.setCurrentStep();
    this.checkInViewMode();

    if (!this.doAllMarkersHaveIds) {
      window.DocNav.rebootAnchorLinksForHeaders();
    }
  };
}
